// Quick spatial lookup: для (lat, lon) находит ближайшую comune по KDTree.
// Используется, чтобы привязать импортированный пин (Idealista-адрес) к
// конкретному comune — после этого пользователь видит метрики этого comune
// прямо из popup пина.
import KDBush from 'kdbush';
import type { ComuneLight } from '@/data';

// Равно подходу из choropleth/idw.ts: equirectangular-проекция на средней
// широте Италии. Точность для расстояний < 5 км — ±0.5%.
const ITALY_LAT_MID = 43;
const LON_SCALE = Math.cos((ITALY_LAT_MID * Math.PI) / 180);
const EARTH_KM_PER_DEG_LAT = 111.32;

export interface NearestComuneIndex {
  tree: KDBush;
  comuni: ComuneLight[];
}

export function buildNearestComuneIndex(comuni: ComuneLight[]): NearestComuneIndex {
  const tree = new KDBush(comuni.length);
  for (const c of comuni) tree.add(c.lat, c.lon * LON_SCALE);
  tree.finish();
  return { tree, comuni };
}

/**
 * Возвращает истат ближайшей comune в радиусе maxDistKm. null — если
 * ничего в радиусе нет (например, импортированный адрес оказался вне Италии
 * или Nominatim вернул мусор).
 */
export function findNearestIstat(
  lat: number,
  lon: number,
  index: NearestComuneIndex,
  maxDistKm = 8,
): string | null {
  const { tree, comuni } = index;
  if (comuni.length === 0) return null;
  const radiusDeg = maxDistKm / EARTH_KM_PER_DEG_LAT;
  const candidateIds = tree.within(lat, lon * LON_SCALE, radiusDeg);
  if (candidateIds.length === 0) return null;
  let bestId = -1;
  let bestDist = Infinity;
  for (const id of candidateIds) {
    const c = comuni[id];
    const dLat = (c.lat - lat) * EARTH_KM_PER_DEG_LAT;
    const dLon = (c.lon - lon) * LON_SCALE * EARTH_KM_PER_DEG_LAT;
    const d = dLat * dLat + dLon * dLon;
    if (d < bestDist) {
      bestDist = d;
      bestId = id;
    }
  }
  return bestId >= 0 ? comuni[bestId].istat : null;
}
